import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Divider,
  IconButton,
} from '@mui/material';
import { Add, Search, Person, People, HowToReg, Work, Event, TrendingUp, BusinessCenter, Email, Phone, AttachMoney, Calculate, Download, Delete, Edit, Save } from '@mui/icons-material';
import { staffAPI, payrollAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import * as XLSX from 'xlsx';

const STATUS_OPTIONS = ['active', 'inactive', 'on_leave', 'terminated', 'resigned'];
const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'intern'];
const DEPARTMENT_OPTIONS = ['Sales', 'Inventory', 'Finance', 'Management', 'IT', 'HR', 'Operations'];

const StaffPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('');
  
  // Dashboard statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onLeave: 0,
    inactive: 0,
    byDepartment: [],
    byEmploymentType: [],
    recentHires: [],
    departmentDetails: []
  });

  // Salary calculation state
  const [salaryMonth, setSalaryMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaryCalculations, setSalaryCalculations] = useState([]);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
  const [globalDeductionDialogOpen, setGlobalDeductionDialogOpen] = useState(false);
  const [globalAllowanceDialogOpen, setGlobalAllowanceDialogOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedDeductionId, setSelectedDeductionId] = useState(null);
  const [isEditingDeduction, setIsEditingDeduction] = useState(false);
  const [commissionAmount, setCommissionAmount] = useState('');
  const [commissionNote, setCommissionNote] = useState('');
  const [deductionName, setDeductionName] = useState('');
  const [deductionAmount, setDeductionAmount] = useState('');
  const [deductionNote, setDeductionNote] = useState('');
  const [allowanceName, setAllowanceName] = useState('');
  const [allowanceAmount, setAllowanceAmount] = useState('');
  const [allowanceNote, setAllowanceNote] = useState('');
  const [taxRate, setTaxRate] = useState(15); // Default 15% tax
  const [globalDeductions, setGlobalDeductions] = useState([]);
  const [globalAllowances, setGlobalAllowances] = useState([]);
  const [excludedEmployees, setExcludedEmployees] = useState([]);
  const [recalcTrigger, setRecalcTrigger] = useState(0);

  const calculateSalaries = useCallback(async () => {
  try {
    const { data } = await staffAPI.getAll({ limit: 1000 });
    const staffList = data.data || [];
    
    // Filter for active and on_leave employees
    const filteredStaff = staffList.filter(s => 
      (s.status === 'active' || s.status === 'on_leave') && !excludedEmployees.includes(s.id)
    );
    
    const calculations = filteredStaff.map(s => {
      const baseSalary = parseFloat(s.salary) || 0;
      const commission = 0;
      
      // Combine individual and global deductions
      const individualDeductions = []; // Individual deductions per employee
      const allDeductions = [...individualDeductions, ...globalDeductions];
      
      // Global allowances (apply to all)
      const applicableGlobalAllowances = globalAllowances;
      
      // Tax is calculated only on base salary
      const tax = (baseSalary * taxRate) / 100;
      
      // Gross = base salary - tax
      const grossSalary = baseSalary - tax;
      
      // Total deductions (calculated as percentage of base salary)
      const totalDeductions = allDeductions.reduce((sum, d) => sum + (baseSalary * d.percentage / 100), 0);
      
      // Total allowances (fixed amounts)
      const totalAllowances = applicableGlobalAllowances.reduce((sum, a) => sum + a.amount, 0);
      
      // Net = gross + commission + allowances - deductions
      const netSalary = grossSalary + commission + totalAllowances - totalDeductions;
      
      return {
        id: s.id,
        company_id: s.company_id,
        name: `${s.first_name} ${s.middle_name || ''} ${s.last_name}`.trim(),
        account_number: s.account_number,
        department: s.department,
        position: s.position,
        status: s.status,
        baseSalary,
        commission,
        allowances: applicableGlobalAllowances,
        totalAllowances,
        deductions: allDeductions,
        individualDeductions,
        globalDeductions: globalDeductions,
        totalDeductions,
        taxRate,
        tax,
        grossSalary,
        netSalary,
      };
    });
    
    setSalaryCalculations(calculations);
  } catch (err) {
    toast.error('Failed to calculate salaries');
    console.error(err);
  }
}, [taxRate, globalDeductions, globalAllowances, excludedEmployees]);

useEffect(() => {
  if (tab === 2) {
    calculateSalaries();
  }
}, [tab, salaryMonth, calculateSalaries, recalcTrigger]);

const handleAddCommission = () => {
  if (!selectedStaffId || !commissionAmount) {
    toast.error('Please select staff and enter commission amount');
    return;
  }
  
  setSalaryCalculations(prev => prev.map(calc => {
    if (calc.id === selectedStaffId) {
      const newCommission = parseFloat(commissionAmount);
      const newTax = (calc.baseSalary * taxRate) / 100;
      const newGross = calc.baseSalary - newTax;
      const newNet = newGross + newCommission + calc.totalAllowances - calc.totalDeductions;
      return {
        ...calc,
        commission: newCommission,
        tax: newTax,
        grossSalary: newGross,
        netSalary: newNet,
      };
    }
    return calc;
  }));
  
  setCommissionDialogOpen(false);
  setCommissionAmount('');
  setCommissionNote('');
  setSelectedStaffId(null);
  toast.success('Commission added successfully');
};

const handleAddDeduction = () => {
  if (!selectedStaffId || !deductionName || !deductionAmount) {
    toast.error('Please fill all required fields');
    return;
  }
  
  setSalaryCalculations(prev => prev.map(calc => {
    if (calc.id === selectedStaffId) {
      if (isEditingDeduction) {
        // Update existing deduction
        const updatedDeductions = calc.individualDeductions.map(d => {
          if (d.id === selectedDeductionId) {
            return {
              ...d,
              name: deductionName,
              percentage: parseFloat(deductionAmount),
              note: deductionNote
            };
          }
          return d;
        });
        const allDeductions = [...updatedDeductions, ...calc.globalDeductions];
        const totalDeductions = allDeductions.reduce((sum, d) => sum + (calc.baseSalary * d.percentage / 100), 0);
        const newNet = calc.grossSalary + calc.commission + calc.totalAllowances - totalDeductions;
        return {
          ...calc,
          individualDeductions: updatedDeductions,
          deductions: allDeductions,
          totalDeductions,
          netSalary: newNet,
        };
      } else {
        // Add new deduction
        const newDeduction = {
          id: Date.now(),
          name: deductionName,
          percentage: parseFloat(deductionAmount),
          note: deductionNote,
          type: 'individual'
        };
        const updatedDeductions = [...calc.individualDeductions, newDeduction];
        const allDeductions = [...updatedDeductions, ...calc.globalDeductions];
        const totalDeductions = allDeductions.reduce((sum, d) => sum + (calc.baseSalary * d.percentage / 100), 0);
        const newNet = calc.grossSalary + calc.commission + calc.totalAllowances - totalDeductions;
        return {
          ...calc,
          individualDeductions: updatedDeductions,
          deductions: allDeductions,
          totalDeductions,
          netSalary: newNet,
        };
      }
    }
    return calc;
  }));
  
  setDeductionDialogOpen(false);
  setDeductionName('');
  setDeductionAmount('');
  setDeductionNote('');
  setSelectedStaffId(null);
  setSelectedDeductionId(null);
  setIsEditingDeduction(false);
  toast.success(isEditingDeduction ? 'Deduction updated successfully' : 'Deduction added successfully');
};

const handleEditDeduction = (staffId, deductionId) => {
  const calc = salaryCalculations.find(c => c.id === staffId);
  if (!calc) return;
  
  const deduction = calc.individualDeductions.find(d => d.id === deductionId);
  if (!deduction) return;
  
  setSelectedStaffId(staffId);
  setSelectedDeductionId(deductionId);
  setDeductionName(deduction.name);
  setDeductionAmount(deduction.percentage.toString());
  setDeductionNote(deduction.note || '');
  setIsEditingDeduction(true);
  setDeductionDialogOpen(true);
};

const handleDeleteDeduction = (staffId, deductionId) => {
  setSalaryCalculations(prev => prev.map(calc => {
    if (calc.id === staffId) {
      const updatedDeductions = calc.individualDeductions.filter(d => d.id !== deductionId);
      const allDeductions = [...updatedDeductions, ...calc.globalDeductions];
      const totalDeductions = allDeductions.reduce((sum, d) => sum + (calc.baseSalary * d.percentage / 100), 0);
      const newNet = calc.grossSalary + calc.commission + calc.totalAllowances - totalDeductions;
      return {
        ...calc,
        individualDeductions: updatedDeductions,
        deductions: allDeductions,
        totalDeductions,
        netSalary: newNet,
      };
    }
    return calc;
  }));
  toast.success('Deduction removed successfully');
};

const handleAddGlobalDeduction = () => {
  if (!deductionName || !deductionAmount) {
    toast.error('Please fill all required fields');
    return;
  }
  
  const newDeduction = {
    id: Date.now(),
    name: deductionName,
    percentage: parseFloat(deductionAmount),
    note: deductionNote
  };
  
  setGlobalDeductions(prev => [...prev, newDeduction]);
  setGlobalDeductionDialogOpen(false);
  setDeductionName('');
  setDeductionAmount('');
  setDeductionNote('');
  setRecalcTrigger(prev => prev + 1);
  toast.success('Global deduction added successfully');
};

const handleRemoveGlobalDeduction = (id) => {
  setGlobalDeductions(prev => prev.filter(d => d.id !== id));
  setRecalcTrigger(prev => prev + 1);
  toast.success('Global deduction removed');
};

const handleRemoveFromList = (staffId) => {
  setExcludedEmployees(prev => [...prev, staffId]);
  toast.success('Employee removed from payroll list');
};

const handleAddGlobalAllowance = () => {
  if (!allowanceName || !allowanceAmount) {
    toast.error('Please fill all required fields');
    return;
  }
  
  const newAllowance = {
    id: Date.now(),
    name: allowanceName,
    amount: parseFloat(allowanceAmount),
    note: allowanceNote
  };
  
  setGlobalAllowances(prev => [...prev, newAllowance]);
  setGlobalAllowanceDialogOpen(false);
  setAllowanceName('');
  setAllowanceAmount('');
  setAllowanceNote('');
  setRecalcTrigger(prev => prev + 1);
  toast.success('Global allowance added successfully');
};

const handleRemoveGlobalAllowance = (id) => {
  setGlobalAllowances(prev => prev.filter(a => a.id !== id));
  setRecalcTrigger(prev => prev + 1);
  toast.success('Global allowance removed');
};

const handleExportToExcel = () => {
  const exportData = salaryCalculations.map(calc => ({
    'Company ID': calc.company_id,
    'Name': calc.name,
    'Account Number': calc.account_number || '',
    'Department': calc.department,
    'Position': calc.position,
    'Status': calc.status,
    'Base Salary': calc.baseSalary,
    'Commission': calc.commission,
    'Tax': calc.tax,
    'Tax Rate': `${taxRate}%`,
    'Gross Salary (After Tax)': calc.grossSalary,
    'Allowances': calc.totalAllowances,
    'Allowance Details': calc.allowances.map(a => `${a.name}: $${a.amount}`).join(', ') || 'None',
    'Other Deductions': calc.totalDeductions,
    'Net Salary': calc.netSalary,
    'Deduction Details': calc.deductions.map(d => `${d.name}: ${d.percentage}%`).join(', ') || 'None',
  }));
  
  // Add totals row
  exportData.push({
    'Company ID': '',
    'Name': 'TOTALS',
    'Account Number': '',
    'Department': '',
    'Position': '',
    'Status': '',
    'Base Salary': salaryCalculations.reduce((sum, c) => sum + c.baseSalary, 0),
    'Commission': salaryCalculations.reduce((sum, c) => sum + c.commission, 0),
    'Tax': salaryCalculations.reduce((sum, c) => sum + c.tax, 0),
    'Tax Rate': `${taxRate}%`,
    'Gross Salary (After Tax)': salaryCalculations.reduce((sum, c) => sum + c.grossSalary, 0),
    'Allowances': salaryCalculations.reduce((sum, c) => sum + c.totalAllowances, 0),
    'Allowance Details': '',
    'Other Deductions': salaryCalculations.reduce((sum, c) => sum + c.totalDeductions, 0),
    'Net Salary': salaryCalculations.reduce((sum, c) => sum + c.netSalary, 0),
    'Deduction Details': '',
  });
  
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary Report');
  
  XLSX.writeFile(workbook, `salary_report_${salaryMonth}.xlsx`);
  toast.success('Salary report exported successfully');
};

const handleSavePayroll = async () => {
  if (salaryCalculations.length === 0) {
    toast.error('No salary calculations to save');
    return;
  }

  try {
    const items = salaryCalculations.map(calc => ({
      staff_id: calc.id,
      company_id: calc.company_id,
      staff_name: calc.name,
      account_number: calc.account_number,
      department: calc.department,
      position: calc.position,
      status: calc.status,
      base_salary: calc.baseSalary,
      tax: calc.tax,
      commission: calc.commission,
      allowances: calc.allowances,
      total_allowances: calc.totalAllowances,
      deductions: calc.deductions,
      individual_deductions: calc.individualDeductions,
      global_deductions: calc.globalDeductions,
      total_deductions: calc.totalDeductions,
      net_salary: calc.netSalary,
    }));

    const payload = {
      payroll_month: salaryMonth,
      tax_rate: taxRate,
      global_deductions: globalDeductions,
      global_allowances: globalAllowances,
      items: items,
    };

    await payrollAPI.create(payload);
    toast.success('Payroll saved successfully');
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to save payroll');
    console.error(err);
  }
};

const fetchAllStaffForStats = async () => {
    try {
      const { data } = await staffAPI.getAll({ limit: 1000 });
      const allStaff = data.data || [];
      
      // Calculate statistics
      const total = allStaff.length;
      const active = allStaff.filter(s => s.status === 'active').length;
      const onLeave = allStaff.filter(s => s.status === 'on_leave').length;
      const inactive = allStaff.filter(s => s.status === 'inactive' || s.status === 'terminated' || s.status === 'resigned').length;
      
      // Department distribution with details
      const deptMap = {};
      allStaff.forEach(s => {
        const dept = s.department || 'Unassigned';
        if (!deptMap[dept]) {
          deptMap[dept] = { total: 0, active: 0, onLeave: 0, staff: [] };
        }
        deptMap[dept].total += 1;
        if (s.status === 'active') deptMap[dept].active += 1;
        if (s.status === 'on_leave') deptMap[dept].onLeave += 1;
        deptMap[dept].staff.push(s);
      });
      const byDepartment = Object.entries(deptMap).map(([name, data]) => ({ name, count: data.total }));
      const departmentDetails = Object.entries(deptMap).map(([name, data]) => ({
        name,
        total: data.total,
        active: data.active,
        onLeave: data.onLeave,
        staff: data.staff.slice(0, 3) // Top 3 staff per department
      }));
      
      // Employment type distribution
      const empMap = {};
      allStaff.forEach(s => {
        const type = s.employment_type || 'unknown';
        empMap[type] = (empMap[type] || 0) + 1;
      });
      const byEmploymentType = Object.entries(empMap).map(([name, count]) => ({ 
        name: name.replace('_', ' '), 
        count 
      }));
      
      // Recent hires (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentHires = allStaff
        .filter(s => s.hire_date && new Date(s.hire_date) >= thirtyDaysAgo)
        .slice(0, 5)
        .map(s => ({
          id: s.id,
          name: `${s.first_name} ${s.last_name}`,
          position: s.position,
          department: s.department,
          date: new Date(s.hire_date).toLocaleDateString()
        }));
      
      setStats({ total, active, onLeave, inactive, byDepartment, byEmploymentType, recentHires, departmentDetails });
    } catch (err) {
      console.error('Failed to fetch staff stats:', err);
    }
  };

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        department: departmentFilter || undefined,
        status: statusFilter || undefined,
        employment_type: employmentTypeFilter || undefined,
      });
      setStaff(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, departmentFilter, statusFilter, employmentTypeFilter]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => { fetchAllStaffForStats(); }, []);

  const columns = [
    {
      key: 'profile_photo_url',
      label: 'Photo',
      render: (value, row) => (
        <Avatar
          src={value ? value : undefined}
          sx={{ width: 40, height: 40 }}
        >
          {!value && <Person />}
        </Avatar>
      ),
    },
    { key: 'company_id', label: 'Company ID', render: (value) => <Typography variant="body2" fontFamily="monospace" fontWeight={600}>{value}</Typography> },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'department', label: 'Department' },
    { key: 'branch', label: 'Branch' },
    { key: 'position', label: 'Position' },
    { key: 'employment_type', label: 'Employment Type', render: (value) => <Chip label={value?.replace('_', ' ')} size="small" /> },
    { key: 'status', label: 'Status', render: (value) => <StatusChip status={value} /> },
    { key: 'phone', label: 'Phone' },
    {
      key: 'id',
      label: 'Actions',
      render: (value) => (
        <Button size="small" variant="outlined" onClick={() => navigate(`/staffs/${value}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
            Staff Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and view all staff members
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/staffs/new')}>
          Add Staff
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tab value={0} label="Overview" />
        <Tab value={1} label="Staff List" />
        <Tab value={2} label="Salary Calculation" />
      </Tabs>

      {/* Overview Tab */}
      {tab === 0 && (
        <Box>
          {/* Dashboard Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Staff
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {stats.total}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <People sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Active Staff
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: 'success.main' }}>
                        {stats.active}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                      <HowToReg sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        On Leave
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: 'warning.main' }}>
                        {stats.onLeave}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                      <Event sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Inactive/Terminated
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: 'error.main' }}>
                        {stats.inactive}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48 }}>
                      <Work sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Staff by Department
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.byDepartment}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1a237e" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Employment Type Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={stats.byEmploymentType}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        <Cell fill="#1a237e" />
                        <Cell fill="#3949ab" />
                        <Cell fill="#7986cb" />
                        <Cell fill="#c5cae9" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Department Details Section */}
          <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BusinessCenter sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Department Overview
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {stats.departmentDetails.map((dept) => (
                  <Grid item xs={12} sm={6} md={4} key={dept.name}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'white' }}>
                          {dept.name}
                        </Typography>
                        <Chip 
                          label={`${dept.total}`} 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.15)', p: 1, borderRadius: 1 }}>
                          <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                            {dept.active}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                            Active
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.15)', p: 1, borderRadius: 1 }}>
                          <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                            {dept.onLeave}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                            On Leave
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Recent Hires Section */}
          {stats.recentHires.length > 0 && (
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Recent Hires (Last 30 Days)
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {stats.recentHires.map((hire) => (
                    <Grid item xs={12} sm={6} md={4} key={hire.id}>
                      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {hire.name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {hire.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hire.position} • {hire.department}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined: {hire.date}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Staff List Tab */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search staff..."
              size="small"
              sx={{ minWidth: 280 }}
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search /> }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={departmentFilter}
                onChange={(event) => { setDepartmentFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {DEPARTMENT_OPTIONS.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Employment Type</InputLabel>
              <Select
                label="Employment Type"
                value={employmentTypeFilter}
                onChange={(event) => { setEmploymentTypeFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {EMPLOYMENT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <DataTable
            columns={columns}
            rows={staff}
            loading={loading}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
            emptyMessage="No staff members found"
          />
        </Box>
      )}

      {/* Salary Calculation Tab */}
      {tab === 2 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Month"
                type="month"
                value={salaryMonth}
                onChange={(e) => setSalaryMonth(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportToExcel}
                fullWidth
                sx={{ height: 56 }}
              >
                Export to Excel
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={<Save />}
                onClick={handleSavePayroll}
                fullWidth
                sx={{ height: 56 }}
              >
                Save Payroll
              </Button>
            </Grid>
          </Grid>

          {/* Global Deductions Section */}
          <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  Global Deductions (Apply to All)
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setGlobalDeductionDialogOpen(true)}
                >
                  Add Global Deduction
                </Button>
              </Box>
              {globalDeductions.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No global deductions added
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {globalDeductions.map((ded) => (
                    <Grid item xs={12} sm={6} md={4} key={ded.id}>
                      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{ded.name}</Typography>
                          <Typography variant="body2" color="primary.main">{ded.percentage}%</Typography>
                          {ded.note && <Typography variant="caption" color="text.secondary">{ded.note}</Typography>}
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveGlobalDeduction(ded.id)}>
                          <Delete />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Global Allowances Section */}
          <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  Global Allowances (Apply to All)
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setGlobalAllowanceDialogOpen(true)}
                >
                  Add Global Allowance
                </Button>
              </Box>
              {globalAllowances.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No global allowances added
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {globalAllowances.map((allow) => (
                    <Grid item xs={12} sm={6} md={4} key={allow.id}>
                      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{allow.name}</Typography>
                          <Typography variant="body2" color="success.main">${allow.amount}</Typography>
                          {allow.note && <Typography variant="caption" color="text.secondary">{allow.note}</Typography>}
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveGlobalAllowance(allow.id)}>
                          <Delete />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Monthly Salary Calculation - {salaryMonth}
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell align="right">Base Salary</TableCell>
                    <TableCell align="right">Tax ({taxRate}%)</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Allowances</TableCell>
                    <TableCell align="right">Deductions</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salaryCalculations.map((calc) => (
                    <TableRow key={calc.id}>
                      <TableCell>{calc.company_id}</TableCell>
                      <TableCell>{calc.name}</TableCell>
                      <TableCell>{calc.account_number || '-'}</TableCell>
                      <TableCell>{calc.department}</TableCell>
                      <TableCell>{calc.position}</TableCell>
                      <TableCell align="right">${calc.baseSalary.toFixed(2)}</TableCell>
                      <TableCell align="right">${calc.tax.toFixed(2)}</TableCell>
                      <TableCell align="right">${calc.commission.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2">${calc.totalAllowances.toFixed(2)}</Typography>
                          {calc.allowances.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {calc.allowances.map(a => `${a.name}`).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2">${calc.totalDeductions.toFixed(2)}</Typography>
                          {calc.individualDeductions.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {calc.individualDeductions.map(d => (
                                <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {d.name} ({d.percentage}%)
                                  </Typography>
                                  <IconButton size="small" onClick={() => handleEditDeduction(calc.id, d.id)} sx={{ p: 0.5 }}>
                                    <Edit sx={{ fontSize: 12 }} />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteDeduction(calc.id, d.id)} sx={{ p: 0.5 }}>
                                    <Delete sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          )}
                          {calc.globalDeductions.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              + Global: {calc.globalDeductions.map(d => `${d.name} ${d.percentage}%`).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>${calc.netSalary.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AttachMoney />}
                            onClick={() => {
                              setSelectedStaffId(calc.id);
                              setCommissionDialogOpen(true);
                            }}
                          >
                            Commission
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedStaffId(calc.id);
                              setDeductionDialogOpen(true);
                            }}
                          >
                            Deduction
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemoveFromList(calc.id)}
                          >
                            Remove
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Subtotal Row */}
                  {salaryCalculations.length > 0 && (
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell colSpan={5} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.baseSalary, 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.tax, 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.commission, 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.totalAllowances, 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.totalDeductions, 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${salaryCalculations.reduce((sum, c) => sum + c.netSalary, 0).toFixed(2)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                  {/* Currency Row */}
                  {salaryCalculations.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          All amounts in Ethiopian Birr (ETB)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {salaryCalculations.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No salary calculations available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onClose={() => setCommissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Commission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Commission Amount"
            type="number"
            fullWidth
            value={commissionAmount}
            onChange={(e) => setCommissionAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Note (optional)"
            fullWidth
            multiline
            rows={3}
            value={commissionNote}
            onChange={(e) => setCommissionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCommission} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Deduction Dialog */}
      <Dialog open={deductionDialogOpen} onClose={() => {
        setDeductionDialogOpen(false);
        setDeductionName('');
        setDeductionAmount('');
        setDeductionNote('');
        setSelectedStaffId(null);
        setSelectedDeductionId(null);
        setIsEditingDeduction(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditingDeduction ? 'Edit Individual Deduction' : 'Add Individual Deduction'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Deduction Name"
            fullWidth
            value={deductionName}
            onChange={(e) => setDeductionName(e.target.value)}
            placeholder="e.g., Insurance, Pension, Loan"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Deduction Percentage"
            type="number"
            fullWidth
            value={deductionAmount}
            onChange={(e) => setDeductionAmount(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Note (optional)"
            fullWidth
            multiline
            rows={3}
            value={deductionNote}
            onChange={(e) => setDeductionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeductionDialogOpen(false);
            setDeductionName('');
            setDeductionAmount('');
            setDeductionNote('');
            setSelectedStaffId(null);
            setSelectedDeductionId(null);
            setIsEditingDeduction(false);
          }}>Cancel</Button>
          <Button onClick={handleAddDeduction} variant="contained">{isEditingDeduction ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Global Deduction Dialog */}
      <Dialog open={globalDeductionDialogOpen} onClose={() => setGlobalDeductionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Global Deduction</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Deduction Name"
            fullWidth
            value={deductionName}
            onChange={(e) => setDeductionName(e.target.value)}
            placeholder="e.g., Health Insurance, Pension Fund"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Deduction Percentage"
            type="number"
            fullWidth
            value={deductionAmount}
            onChange={(e) => setDeductionAmount(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Note (optional)"
            fullWidth
            multiline
            rows={3}
            value={deductionNote}
            onChange={(e) => setDeductionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlobalDeductionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddGlobalDeduction} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Global Allowance Dialog */}
      <Dialog open={globalAllowanceDialogOpen} onClose={() => setGlobalAllowanceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Global Allowance</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Allowance Name"
            fullWidth
            value={allowanceName}
            onChange={(e) => setAllowanceName(e.target.value)}
            placeholder="e.g., Housing, Transport, Meal"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Allowance Amount"
            type="number"
            fullWidth
            value={allowanceAmount}
            onChange={(e) => setAllowanceAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Note (optional)"
            fullWidth
            multiline
            rows={3}
            value={allowanceNote}
            onChange={(e) => setAllowanceNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlobalAllowanceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddGlobalAllowance} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPage;
